/**
 * Daily Brief — the JAS board of small market widgets
 * (`docs/plans/daily-brief.md`, mock `docs/daily-brief-mock.html`).
 *
 * A 12-column `react-grid-layout` board: widgets are dragged by their title
 * bar, resized from the bottom-right corner, added from + Widget and removed
 * with ×. Every edit goes through the pure editors in `lib/brief-board.ts`,
 * which decide whether it lands in the preset (the author) or in the member's
 * own board on top of it; this file only says which edit was made.
 *
 * On a phone the board is one column in reading order and the sizes are
 * ignored — there is no room to drag, and a 4-column widget is a phone's width.
 */
import { useMemo, useState, type CSSProperties } from "react";
import GridLayout, { WidthProvider, type Layout as GridItem } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import type { BriefBoardConfig, BriefWidget } from "@qufolio/model";
import {
  GRID, WIDGET_META, addWidget, boardFor, effectiveState, hasMine, memberAdded, newerPresetAvailable, removeWidget, resetToPreset,
  setWidgetSettings, setWidgets, type EditContext, type ListName, type WidgetType,
} from "../lib/brief-board";
import { useIsNarrow } from "../lib/use-narrow";
import { Chip } from "./brief/cells";
import { ListEditor } from "./brief/ListEditor";
import { MarketPerfWidget } from "./brief/MarketPerfWidget";
import { NhNlWidget } from "./brief/NhNlWidget";
import { PerfTableWidget } from "./brief/PerfTableWidget";
import { SectorPerfWidget } from "./brief/SectorPerfWidget";
import { SectorRaceWidget } from "./brief/SectorRaceWidget";
import { DRAG_HANDLE, NO_DRAG } from "./brief/WidgetCard";
import { DataCards } from "./DataCards";
import { WIDGET_CHIP } from "../lib/mobile-brief";

const Grid = WidthProvider(GridLayout);

export interface BriefBoardViewProps {
  config: BriefBoardConfig;
  /** Edits go to the member's own board (`isMemberCopy`), not the preset. */
  member: boolean;
  /** The layout's `source.version`, for "a newer preset is available". */
  sourceVersion?: number;
  /** Absent: the board is read-only — no drag, resize, add, remove or list edit. */
  onChange?: (next: BriefBoardConfig) => void;
  /**
   * Opens a symbol in the linked chart — a race row hands `XLK/SPY`. Absent, a
   * row is not a link. `SectionGrid` passes its `onSelectSymbol`, the
   * screener's pick-to-chart.
   */
  onOpenSymbol?: (symbol: string) => void;
  /**
   * The touch shell: one widget a card, swiped, in reading order, with no board
   * bar (`docs/features/mobile.md`, "The briefs are cards too"). Pass no
   * `onChange` with it — touch is read-only.
   */
  cards?: boolean;
}

/**
 * The widgets whose place or size moved. `react-grid-layout` reports a layout
 * on mount and on every click of a title bar; only a real move is an edit,
 * or a member who merely looked would get a board of their own.
 */
export function movedWidgets(widgets: readonly BriefWidget[], layout: readonly GridItem[]): BriefWidget[] | null {
  const at = new Map(layout.map((l) => [l.i, l]));
  let moved = false;
  const next = widgets.map((w) => {
    const l = at.get(w.id);
    if (!l || (l.x === w.x && l.y === w.y && l.w === w.w && l.h === w.h)) return w;
    moved = true;
    return { ...w, x: l.x, y: l.y, w: l.w, h: l.h };
  });
  return moved ? next : null;
}

/** Reading order: top to bottom, left to right. */
const readingOrder = (a: BriefWidget, b: BriefWidget) => a.y - b.y || a.x - b.x;

export function BriefBoardView({ config: stored, member, sourceVersion, onChange, onOpenSymbol, cards = false }: BriefBoardViewProps) {
  const narrow = useIsNarrow();
  const [menu, setMenu] = useState(false);
  const [editing, setEditing] = useState<{ list: ListName; title: string } | null>(null);
  /** The card on screen, in `cards` mode. */
  const [card, setCard] = useState("");
  const ctx: EditContext = { member, sourceVersion };
  const editable = !!onChange;
  // What is drawn and what is edited are one state: a stray `mine` on a board
  // that is no member's copy is neither (`boardFor`).
  const config = useMemo(() => boardFor(stored, member), [stored, member]);
  const state = useMemo(() => effectiveState(config), [config]);
  const widgets = state.widgets;

  const yours = useMemo(() => {
    const of = (name: ListName) =>
      new Set(memberAdded(config.preset.lists?.[name], config.mine?.lists?.[name]).map((i) => i.symbol));
    return { daily: of("daily"), etf: of("etf") };
  }, [config]);

  const layout: GridItem[] = widgets.map((w) => {
    const [minW, minH] = WIDGET_META[w.type].min;
    return { i: w.id, x: w.x, y: w.y, w: w.w, h: w.h, minW, minH };
  });

  const commitLayout = (next: GridItem[]) => {
    const moved = movedWidgets(widgets, next);
    if (moved && onChange) onChange(setWidgets(config, ctx, moved));
  };

  const renderWidget = (w: BriefWidget) => {
    const onSettings = onChange ? (s: Record<string, unknown>) => onChange(setWidgetSettings(config, ctx, w.id, s)) : undefined;
    const onRemove = onChange ? () => onChange(removeWidget(config, ctx, w.id)) : undefined;
    const common = { widget: w, onSettings, onRemove };
    switch (w.type) {
      case "market":
        return <MarketPerfWidget {...common} />;
      case "sectors":
        return <SectorPerfWidget {...common} />;
      case "nhnl":
        return <NhNlWidget {...common} />;
      case "race":
        return <SectorRaceWidget {...common} onOpenSymbol={onOpenSymbol} />;
      case "daily":
      case "etf": {
        const list = state.lists?.[w.type] ?? { items: [], categories: [] };
        return (
          <PerfTableWidget
            {...common}
            list={{ items: list.items ?? [], categories: list.categories ?? [] }}
            yours={yours[w.type]}
            onOpenSymbol={onOpenSymbol}
            onEditList={onChange ? () => setEditing({ list: w.type as ListName, title: WIDGET_META[w.type].title }) : undefined}
          />
        );
      }
    }
  };

  const mine = member && hasMine(config);

  if (cards) {
    const ordered = [...widgets].sort(readingOrder);
    const active = ordered.some((w) => w.id === card) ? card : ordered[0]?.id ?? "";
    return (
      <div style={CARDS_ROOT} className="qf-brief">
        <DataCards
          labels={ordered.map((w) => ({ id: w.id, label: WIDGET_META[w.type].title, chip: WIDGET_CHIP[w.type] }))}
          active={active}
          onActive={setCard}
          strip={false}
          render={(id) => {
            const w = ordered.find((x) => x.id === id);
            return w ? <div style={{ height: "100%" }}>{renderWidget(w)}</div> : null;
          }}
        />
      </div>
    );
  }

  return (
    <div style={ROOT} className="qf-brief">
      <style>{BOARD_CSS}</style>
      <div style={BAR}>
        {editable && (
          <span style={{ position: "relative" }}>
            <Chip primary menu={menu} onClick={() => setMenu((m) => !m)}>+ Widget</Chip>
            {menu && (
              <div style={MENU} role="menu">
                {(Object.keys(WIDGET_META) as WidgetType[]).map((type) => (
                  <button
                    key={type}
                    type="button"
                    role="menuitem"
                    style={MENU_ITEM}
                    onClick={() => {
                      setMenu(false);
                      onChange(addWidget(config, ctx, type));
                    }}
                  >
                    {WIDGET_META[type].title}
                    <span style={MUTED}>{WIDGET_META[type].size.join("×")}</span>
                  </button>
                ))}
              </div>
            )}
          </span>
        )}
        <span style={MUTED}>{mine ? "Your board" : "Preset"}</span>
        <span style={{ flex: 1 }} />
        {newerPresetAvailable(config, sourceVersion) && (
          <span style={NOTICE} role="status">
            A newer preset is available
            {editable && (
              <>
                {" · "}
                <button type="button" style={LINK} onClick={() => onChange(resetToPreset(config))}>Reset</button>
              </>
            )}
          </span>
        )}
        {editable && mine && <Chip onClick={() => onChange(resetToPreset(config))}>Reset to preset</Chip>}
      </div>

      {narrow ? (
        <div style={STACK}>
          {[...widgets].sort(readingOrder).map((w) => (
            <div key={w.id}>{renderWidget(w)}</div>
          ))}
        </div>
      ) : (
        <Grid
          className="qf-brief-grid"
          layout={layout}
          cols={GRID.cols}
          rowHeight={GRID.rowHeight}
          margin={[GRID.margin, GRID.margin]}
          containerPadding={[0, 0]}
          compactType="vertical"
          draggableHandle={`.${DRAG_HANDLE}`}
          draggableCancel={`.${NO_DRAG}`}
          resizeHandles={["se"]}
          isDraggable={editable}
          isResizable={editable}
          onDragStop={commitLayout}
          onResizeStop={commitLayout}
        >
          {widgets.map((w) => (
            <div key={w.id}>{renderWidget(w)}</div>
          ))}
        </Grid>
      )}

      {editing && onChange && (
        <ListEditor
          title={editing.title}
          list={editing.list}
          config={config}
          ctx={ctx}
          onChange={onChange}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

/** The library's placeholder is red; the board's is the accent, as the mock's ghost. */
const BOARD_CSS =
  ".qf-brief .react-grid-item.react-grid-placeholder{background:var(--qf-accent);opacity:.15;border-radius:10px}" +
  ".qf-brief .react-grid-item>.react-resizable-handle::after{border-color:var(--qf-text-muted)}";

const ROOT: CSSProperties = {
  width: "100%", height: "100%", overflow: "auto", boxSizing: "border-box", padding: 8,
  background: "var(--qf-background)", color: "var(--qf-text)", fontSize: 13,
};
const CARDS_ROOT: CSSProperties = {
  width: "100%", height: "100%", display: "flex", flexDirection: "column", minHeight: 0,
  background: "var(--qf-background)", color: "var(--qf-text)", fontSize: 13,
};
const BAR: CSSProperties = {
  display: "flex", gap: 10, alignItems: "center", padding: "6px 10px", marginBottom: 8, flexWrap: "wrap",
  background: "var(--qf-surface)", border: "1px solid var(--qf-border)", borderRadius: 8,
};
const MUTED: CSSProperties = { color: "var(--qf-text-muted)", fontSize: 13 };
const MENU: CSSProperties = {
  position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 50, minWidth: 240, padding: 4,
  background: "var(--qf-surface)", border: "1px solid var(--qf-border)", borderRadius: 8,
  display: "flex", flexDirection: "column",
};
const MENU_ITEM: CSSProperties = {
  display: "flex", justifyContent: "space-between", gap: 12, padding: "6px 10px", borderRadius: 6,
  background: "none", border: "none", color: "var(--qf-text)", cursor: "pointer", font: "inherit", textAlign: "left",
};
const NOTICE: CSSProperties = { color: "var(--qf-accent)", fontSize: 13 };
const LINK: CSSProperties = {
  background: "none", border: "none", padding: 0, color: "var(--qf-accent)", textDecoration: "underline",
  cursor: "pointer", font: "inherit",
};
const STACK: CSSProperties = { display: "flex", flexDirection: "column", gap: GRID.margin };
